import React, { Children, PropsWithChildren, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { BaseDiv } from '../../common/BaseElements';
import useEmblaCarousel from 'embla-carousel-react';
import { CarouselPagination } from './CarouselPagination';
import { AllowableGutterEora, eoraSpaces } from '../../fundamentals/spaces/space';
import { CarouselArrows } from './CarouselArrows';
import { CarouselNavType, CarouselPaginationPosition, CarouselVariant } from './carouselTypes';

export interface ICarouselProps {
  navType?: CarouselNavType;
  variant?: CarouselVariant;
  paginationPosition?: CarouselPaginationPosition;
  slideGap?: AllowableGutterEora;
  loop?: boolean;
}

const CarouselContainer = styled(BaseDiv)`
  overflow: hidden;
  position: relative;
`;

const CarouselSlidesContainer = styled(BaseDiv)`
  display: flex;
`;

const Slide = styled(BaseDiv)<{ last: boolean; slideGap?: AllowableGutterEora; loop: boolean }>`
  flex: 0 0 100%;
  margin-right: ${({ last, slideGap, loop }) => {
    if (last && !loop) return 0;
    if (slideGap) return eoraSpaces[slideGap];
    return 0;
  }};
`;

const Pagination = styled(CarouselPagination)<Pick<ICarouselProps, 'paginationPosition'>>`
  ${({ paginationPosition }) =>
    paginationPosition === 'inside' &&
    css`
      position: absolute;
    `}
`;

export const Carousel: React.FC<PropsWithChildren<ICarouselProps>> = ({
  navType = 'arrows',
  variant = 'light',
  paginationPosition = 'outside',
  loop = false,
  children,
  slideGap,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'Carousel');
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop });
  const [total, setTotal] = React.useState(0);
  const [currentSlide, setCurrentSlide] = React.useState(0);

  useEffect(() => {
    setTotal(Children.count(children));
  }, [children]);

  useEffect(() => {
    /* istanbul ignore next */
    emblaApi?.on('select', (api) => setCurrentSlide(api.selectedScrollSnap()));
  }, [emblaApi]);

  if (total === 0) return <></>;

  if (total === 1) {
    return (
      <Slide last {...generateTestId(`Slide.0`)} loop={false}>
        {children}
      </Slide>
    );
  }

  return (
    <CarouselContainer {...generateTestId()} {...rest} ref={emblaRef}>
      <CarouselSlidesContainer {...generateTestId('SlidesContainer')}>
        {Children.map(
          (children as React.ReactElement[]).filter((c) => !!c),
          (child, i) => (
            <Slide {...generateTestId(`Slide.${i}`)} last={i === total - 1} slideGap={slideGap} loop={loop}>
              {React.cloneElement(child)}
            </Slide>
          )
        )}
      </CarouselSlidesContainer>
      {total > 1 && navType === 'arrows' && <CarouselArrows variant={variant} pagingApi={emblaApi} />}
      {total > 1 && (
        <Pagination
          totalPages={total}
          currentPage={currentSlide}
          variant={variant}
          nested={paginationPosition === 'inside'}
          paginationPosition={paginationPosition}
        />
      )}
    </CarouselContainer>
  );
};
